import React, { useEffect, useState, useCallback } from "react";
import { api, fileUrl, receiptUrl } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { STATUS_FLOW, STATUS_META, statusLabel } from "@/lib/status";
import { Loader2, Search, FileText, Users, Clock, IndianRupee, CheckCircle2, ExternalLink, XCircle, UserPlus, ShieldCheck, Trash2, Mail, Download, Printer, Pencil, History, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { can, canReview, normRole, ROLE_LABELS, isStaff } from "@/lib/permissions";

const FILTERS = [
  { key: "all", label: "All" },
  ...STATUS_FLOW.map((s) => ({ key: s, label: statusLabel(s) })),
  { key: "rejected", label: "Rejected" },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const review = canReview(user?.role);
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showTeam, setShowTeam] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get("/admin/applications", { params: { status: filter, search: search || undefined, page, limit } }),
        api.get("/admin/stats"),
      ]);
      setApps(a.data.items);
      setTotal(a.data.total);
      setStats(s.data);
    } catch {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, [filter, search, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter, search]);
  useEffect(() => { if (searchParams.get("team") === "1") setShowTeam(true); }, [searchParams]);

  return (
    <div className="min-h-screen bg-[#f8faf8]">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-head font-extrabold text-2xl sm:text-3xl text-emerald-950">Admin Console</h1>
            <p className="mt-1.5 text-slate-500">Review, verify and update farmer subsidy applications.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="rounded-full text-emerald-700 h-10" onClick={() => navigate("/admin")} data-testid="back-to-console"><LayoutDashboard className="h-4 w-4 mr-1.5" /> Console</Button>
            {can(user?.role, "user_management", "view") && (
              <Button variant="outline" className="rounded-full border-emerald-200 text-emerald-700 h-10" onClick={() => setShowTeam(true)} data-testid="manage-team-button">
                <Users className="h-4 w-4 mr-1.5" /> Manage Team
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} tint="#eaf5ee" color="#1f6b3b" label="Total Applications" value={stats?.total ?? "—"} testid="stat-total" />
          <StatCard icon={Clock} tint="#fef3c7" color="#b45309" label="Pending Review" value={stats?.pending ?? "—"} testid="stat-pending" />
          <StatCard icon={CheckCircle2} tint="#dcfce7" color="#15803d" label="Sanctioned" value={stats?.sanctioned_count ?? "—"} testid="stat-sanctioned" />
          <StatCard icon={IndianRupee} tint="#e6f0fb" color="#2563eb" label="Sanctioned Value" value={stats ? `₹ ${Number(stats.sanctioned_amount).toLocaleString("en-IN")}` : "—"} testid="stat-amount" />
        </div>

        {/* Controls */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="rounded-full bg-emerald-50 flex-wrap h-auto p-1">
              {FILTERS.map((f) => <TabsTrigger key={f.key} value={f.key} className="rounded-full data-[state=active]:bg-white data-[state=active]:text-emerald-800 text-xs" data-testid={`filter-${f.key}`}>{f.label}</TabsTrigger>)}
            </TabsList>
          </Tabs>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ref, phone or name" className="pl-9 rounded-full h-10" data-testid="admin-search-input" />
          </div>
        </div>

        {/* Table */}
        <Card className="mt-6 rounded-3xl border-emerald-100 overflow-hidden">
          {loading ? (
            <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
          ) : apps.length === 0 ? (
            <div className="py-20 text-center text-slate-400" data-testid="admin-empty">No applications found</div>
          ) : (
            <Table data-testid="admin-applications-table">
              <TableHeader>
                <TableRow className="bg-[#f1f6f2] hover:bg-[#f1f6f2]">
                  <TableHead>Application No</TableHead>
                  <TableHead>Applicant</TableHead>
                  <TableHead className="hidden sm:table-cell">Scheme</TableHead>
                  <TableHead className="hidden md:table-cell">Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apps.map((a) => (
                  <TableRow key={a.id} className="hover:bg-emerald-50/40" data-testid={`app-row-${a.number}`}>
                    <TableCell className="font-mono text-xs text-slate-600">{a.number}</TableCell>
                    <TableCell className="font-medium text-slate-800">{a.applicant?.full_name || "—"}</TableCell>
                    <TableCell className="hidden sm:table-cell text-slate-500">{a.scheme}</TableCell>
                    <TableCell className="hidden md:table-cell text-slate-500">{a.applicant?.mobile || a.user_phone || a.user_email || "—"}</TableCell>
                    <TableCell><span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${STATUS_META[a.status]?.cls}`}>{statusLabel(a.status)}</span></TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="outline" className="rounded-full border-emerald-200 text-emerald-700 h-8" onClick={() => setSelected(a)} data-testid={`review-${a.number}`}>Review</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!loading && total > limit && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-emerald-100 bg-white" data-testid="admin-pagination">
              <span className="text-xs text-slate-500" data-testid="admin-page-info">Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-full border-emerald-200 text-emerald-700 h-8" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-prev-page">Previous</Button>
                <Button variant="outline" size="sm" className="rounded-full border-emerald-200 text-emerald-700 h-8" disabled={page * limit >= total} onClick={() => setPage((p) => p + 1)} data-testid="admin-next-page">Next</Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      <ReviewDialog app={selected} review={review} onClose={() => setSelected(null)} onUpdated={() => { setSelected(null); load(); }} />
      <TeamDialog open={showTeam} onClose={() => { setShowTeam(false); if (searchParams.get("team")) setSearchParams({}); }} />
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, tint, color, testid }) => (
  <Card className="rounded-2xl border-emerald-100 p-5" data-testid={testid}>
    <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ backgroundColor: tint }}><Icon className="h-5 w-5" style={{ color }} /></div>
    <div className="mt-3 font-head font-extrabold text-2xl text-emerald-950">{value}</div>
    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
  </Card>
);

const ReviewDialog = ({ app, onClose, onUpdated, review }) => {
  const [status, setStatus] = useState("");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [reason, setReason] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (app) {
      setStatus(app.status); setRemark(""); setEditing(false); setReason("");
      setEditData({ applicant: { ...(app.applicant || {}) }, address: { ...(app.address || {}) }, bank: { ...(app.bank || {}) }, scheme_data: { ...(app.scheme_data || {}) } });
    }
  }, [app]);
  if (!app) return null;

  const setEd = (sec, key, val) => setEditData((d) => ({ ...d, [sec]: { ...d[sec], [key]: val } }));

  const saveEdit = async () => {
    if (!reason.trim()) { toast.error("Please add a reason for the edit (audit trail)"); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/admin/applications/${app.id}/edit`, { ...editData, reason });
      toast.success("Application updated — original record & PDF preserved");
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Edit failed");
    } finally {
      setSavingEdit(false);
    }
  };

  const update = async (forceStatus) => {
    const newStatus = forceStatus || status;
    setSaving(true);
    try {
      await api.patch(`/admin/applications/${app.id}/status`, { status: newStatus, remark: remark || undefined });
      toast.success(`Status updated to ${statusLabel(newStatus)}`);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const docs = Object.entries(app.documents || {});

  return (
    <Dialog open={!!app} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-emerald-950 flex items-center gap-2">
            {app.applicant?.full_name || "Applicant"} <span className="font-mono text-xs text-slate-400">{app.number}</span>
          </DialogTitle>
          <DialogDescription>{app.scheme_name || app.scheme} — review details, documents and update status.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">
            <a href={receiptUrl(app.id)} target="_blank" rel="noreferrer" data-testid="admin-download-pdf"><Button size="sm" className="rounded-full bg-brand hover:bg-emerald-800 text-white h-8"><Download className="h-4 w-4 mr-1.5" /> Download PDF</Button></a>
            <Button size="sm" variant="outline" className="rounded-full border-emerald-200 text-emerald-700 h-8" onClick={() => window.open(receiptUrl(app.id), "_blank")} data-testid="admin-print"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">The downloaded PDF always reflects the original submitted record.</span>
            {review && (!editing ? (
              <Button size="sm" variant="outline" className="rounded-full border-amber-300 text-amber-700 h-8" onClick={() => setEditing(true)} data-testid="admin-edit-toggle"><Pencil className="h-4 w-4 mr-1.5" /> Edit Details</Button>
            ) : (
              <Button size="sm" variant="ghost" className="rounded-full text-slate-500 h-8" onClick={() => setEditing(false)} data-testid="admin-edit-cancel">Cancel</Button>
            ))}
          </div>

          {!editing ? (
            <>
              <Section title="Applicant" data={app.applicant} />
              <Section title="Address" data={app.address} />
              <Section title="Bank" data={app.bank} />
              <Section title="Scheme Details" data={app.scheme_data} />
            </>
          ) : (
            <div className="space-y-5" data-testid="admin-edit-form">
              {["applicant", "address", "bank", "scheme_data"].map((sec) => (
                Object.keys(editData[sec] || {}).length > 0 && (
                  <div key={sec}>
                    <h4 className="font-head font-semibold text-emerald-950 mb-2 text-sm uppercase tracking-wide">{sec.replace("_", " ")}</h4>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {Object.entries(editData[sec]).map(([k, v]) => (
                        <div key={k}>
                          <Label className="text-slate-500 text-xs capitalize">{k.replace(/_/g, " ")}</Label>
                          <Input value={v ?? ""} onChange={(e) => setEd(sec, k, e.target.value)} className="mt-1 h-10 rounded-xl" data-testid={`edit-${sec}-${k}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ))}
              <div>
                <Label className="text-slate-600 text-xs">Reason for edit (recorded in audit trail) *</Label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 rounded-xl min-h-[40px]" placeholder="e.g. Corrected applicant name per Aadhaar" data-testid="edit-reason-input" />
              </div>
              <Button onClick={saveEdit} disabled={savingEdit} className="rounded-full bg-brand hover:bg-emerald-800 text-white" data-testid="admin-edit-save">
                {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
              </Button>
            </div>
          )}

          <div>
            <h4 className="font-head font-semibold text-emerald-950 mb-2 text-sm uppercase tracking-wide">Documents</h4>
            {docs.length === 0 ? <p className="text-sm text-slate-400">No documents</p> : (
              <div className="flex flex-wrap gap-2">
                {docs.map(([k, v]) => (
                  <a key={k} href={fileUrl(v.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs text-emerald-700 hover:bg-emerald-100" data-testid={`doc-${k}`}>
                    <FileText className="h-3.5 w-3.5" /> {k.replace(/_/g, " ")} <ExternalLink className="h-3 w-3" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {app.remarks?.length > 0 && (
            <div>
              <h4 className="font-head font-semibold text-emerald-950 mb-2 text-sm uppercase tracking-wide">History</h4>
              <div className="space-y-2">
                {app.remarks.map((r, i) => (
                  <div key={`${r.at}-${i}`} className="text-sm bg-slate-50 rounded-xl px-3 py-2 flex justify-between gap-3">
                    <span className="text-slate-600">{statusLabel(r.status)}{r.remark ? ` — ${r.remark}` : ""}</span>
                    <span className="text-xs text-slate-400 shrink-0">{new Date(r.at).toLocaleDateString("en-IN")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {app.audit_log?.length > 0 && (
            <div>
              <h4 className="font-head font-semibold text-emerald-950 mb-2 text-sm uppercase tracking-wide flex items-center gap-1.5"><History className="h-4 w-4 text-amber-600" /> Edit Audit Trail</h4>
              <div className="space-y-2" data-testid="audit-log">
                {app.audit_log.map((e, i) => (
                  <div key={`${e.at}-${i}`} className="text-sm bg-amber-50/60 border border-amber-100 rounded-xl px-3 py-2">
                    <div className="flex justify-between gap-3 text-xs text-slate-500">
                      <span>{e.by}{e.reason ? ` — ${e.reason}` : ""}</span>
                      <span className="shrink-0">{new Date(e.at).toLocaleString("en-IN")}</span>
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {e.changes.map((c, j) => (
                        <div key={`${c.section}-${c.field}-${j}`} className="text-xs text-slate-600"><span className="capitalize text-slate-400">{c.field.replace(/_/g, " ")}:</span> <span className="line-through text-rose-500">{c.from || "—"}</span> → <span className="text-emerald-700 font-medium">{c.to}</span></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Update controls */}
          {review && (
          <div className="rounded-2xl bg-[#f1f6f2] p-4 space-y-3">
            <h4 className="font-head font-semibold text-emerald-950 text-sm">Update Status</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-600 text-xs">Stage</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="mt-1 h-10 rounded-xl bg-white" data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[...STATUS_FLOW, "rejected"].map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600 text-xs">Remark (optional)</Label>
                <Textarea value={remark} onChange={(e) => setRemark(e.target.value)} className="mt-1 rounded-xl bg-white min-h-[40px]" placeholder="Note to farmer" data-testid="remark-input" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={() => update()} disabled={saving} className="rounded-full bg-brand hover:bg-emerald-800 text-white" data-testid="save-status-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Update"}
              </Button>
              <Button onClick={() => update("sanctioned")} disabled={saving} variant="outline" className="rounded-full border-emerald-300 text-emerald-700" data-testid="admin-approve-button"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Sanction</Button>
              <Button onClick={() => update("rejected")} disabled={saving} variant="outline" className="rounded-full border-rose-300 text-rose-600" data-testid="admin-reject-button"><XCircle className="h-4 w-4 mr-1.5" /> Reject</Button>
            </div>
          </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, data }) => {
  const entries = Object.entries(data || {}).filter(([, v]) => v !== "" && v != null);
  if (entries.length === 0) return null;
  return (
    <div>
      <h4 className="font-head font-semibold text-emerald-950 mb-2 text-sm uppercase tracking-wide">{title}</h4>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 text-sm border-b border-dashed border-slate-100 py-1">
            <span className="text-slate-400 capitalize">{k.replace(/_/g, " ")}</span>
            <span className="text-slate-700 text-right font-medium">{String(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};


const TeamDialog = ({ open, onClose }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [newRole, setNewRole] = useState("field_executive");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/team");
      setData(res.data);
    } catch {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const assignRole = async (targetEmail, role) => {
    const target = (targetEmail || "").trim().toLowerCase();
    if (!target) return;
    setBusy(target);
    try {
      await api.post("/admin/team/role", { email: target, role });
      toast.success(`${target} set to ${ROLE_LABELS[role]}`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    } finally {
      setBusy("");
    }
  };

  const revoke = async (target) => {
    setBusy(target);
    try {
      await api.post("/admin/team/revoke", { email: target });
      toast.success(`Access revoked for ${target}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke");
    } finally {
      setBusy("");
    }
  };

  const canAssign = data?.can_assign_roles;
  const roleOptions = data?.roles || [];
  const protectedSet = new Set(data?.protected || []);
  const staff = (data?.users || []).filter((u) => isStaff(u.role));
  const farmers = (data?.users || []).filter((u) => !isStaff(u.role));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="team-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-emerald-950 flex items-center gap-2"><Users className="h-5 w-5 text-emerald-700" /> Manage Team & Roles</DialogTitle>
          <DialogDescription>{canAssign ? "Assign roles to staff and channel partners. Only a Super Admin can change roles." : "View your team. Only a Super Admin can change roles."}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="grid place-items-center py-12"><Loader2 className="h-7 w-7 animate-spin text-emerald-700" /></div>
        ) : (
          <div className="space-y-6">
            {canAssign && (
              <div className="rounded-2xl bg-[#f1f6f2] p-4">
                <Label className="text-slate-600 text-sm">Assign a role by email</Label>
                <div className="mt-2 flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@email.com" className="pl-9 rounded-xl bg-white h-11" data-testid="team-email-input" />
                  </div>
                  <Select value={newRole} onValueChange={setNewRole}>
                    <SelectTrigger className="h-11 rounded-xl bg-white sm:w-52" data-testid="team-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{roleOptions.filter((r) => r.value !== "farmer").map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button onClick={() => assignRole(email, newRole)} disabled={!!busy} className="rounded-full bg-brand hover:bg-emerald-800 text-white px-5 h-11" data-testid="team-assign-button">
                    {busy === email.trim().toLowerCase() ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1.5" /> Assign</>}
                  </Button>
                </div>
              </div>
            )}

            {/* Staff & Partners */}
            <div>
              <h4 className="font-head font-semibold text-emerald-950 mb-2 text-sm uppercase tracking-wide flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-700" /> Staff & Partners ({staff.length})</h4>
              <div className="space-y-2" data-testid="staff-list">
                {staff.length === 0 && <p className="text-sm text-slate-400">No staff yet.</p>}
                {staff.map((u) => {
                  const isProtected = protectedSet.has((u.email || "").toLowerCase());
                  const isSelf = (u.email || "").toLowerCase() === (user?.email || "").toLowerCase();
                  return (
                    <div key={u.id} className="flex items-center justify-between gap-3 rounded-xl border border-emerald-100 bg-white px-4 py-2.5">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{u.name || u.email || u.phone}</div>
                        <div className="text-xs text-slate-400 truncate">{u.email || u.phone}{isProtected && " · protected"}{isSelf && " · you"}</div>
                      </div>
                      {canAssign && u.email ? (
                        <Select value={normRole(u.role)} onValueChange={(r) => assignRole(u.email, r)} disabled={isProtected || isSelf || !!busy}>
                          <SelectTrigger className="h-9 rounded-full w-44 bg-white text-xs" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{roleOptions.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">{ROLE_LABELS[normRole(u.role)]}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pending invites */}
            {data?.pending?.length > 0 && (
              <div>
                <h4 className="font-head font-semibold text-emerald-950 mb-2 text-sm uppercase tracking-wide">Invited (not signed in yet)</h4>
                <div className="space-y-2">
                  {data.pending.map((p) => (
                    <div key={p.email} className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-amber-200 bg-amber-50/50 px-4 py-2.5">
                      <span className="text-sm text-amber-800 truncate">{p.email} · <span className="text-amber-600">{ROLE_LABELS[normRole(p.role)]}</span></span>
                      {canAssign && <Button variant="ghost" size="sm" className="rounded-full text-rose-600 hover:bg-rose-50 h-8" disabled={!!busy} onClick={() => revoke(p.email)} data-testid={`revoke-${p.email}`}><Trash2 className="h-4 w-4 mr-1" /> Cancel</Button>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Farmers -> promote to staff */}
            {canAssign && (
              <div>
                <h4 className="font-head font-semibold text-emerald-950 mb-2 text-sm uppercase tracking-wide flex items-center gap-1.5"><Users className="h-4 w-4 text-slate-500" /> Farmers ({farmers.length})</h4>
                <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="farmer-list">
                  {farmers.length === 0 && <p className="text-sm text-slate-400">No farmer accounts yet.</p>}
                  {farmers.map((u) => (
                    <div key={u.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white px-4 py-2.5">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{u.name || u.email || u.phone}</div>
                        <div className="text-xs text-slate-400 truncate">{u.email || u.phone}</div>
                      </div>
                      <Button variant="outline" size="sm" className="rounded-full border-emerald-200 text-emerald-700 h-8" disabled={!!busy || !u.email} onClick={() => assignRole(u.email, "field_executive")} data-testid={`promote-${u.email}`}>
                        <ShieldCheck className="h-4 w-4 mr-1" /> Make Staff
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
